import { Checkbox, Grid, GridColumn, GridRow, Input, Select, SelectOptionType } from '@bp/ui-components';
import { useFormikContext } from 'formik';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select';
import { useCreateSelectOptions } from '../../../../hooks/useCreateSelectOptions';
import { useLoadBasicData } from '../../../../hooks/useLoadBasicData';
import { LessonEditStatus } from '../../../../types/planung-graphql-client-defs';
import { LessonTableType } from '../../TimetableVersion/Table/TimetableVersionLessonsTable';
import { LessonFormType } from '../../types';

type LessonBasicsFormProps = {
  status: LessonTableType['editStatus'];
  timetableDisabled?: boolean | null;
};

export const LessonBasicsForm = ({ status, timetableDisabled }: LessonBasicsFormProps) => {
  const { t } = useTranslation();

  const { setFieldTouched, setFieldValue, values, handleChange, errors, touched } = useFormikContext<LessonFormType>();

  const { subjectData } = useLoadBasicData({ pause: false });

  const subjectsOptions = useCreateSelectOptions(
    subjectData?.subjects.filter((s) => {
      return s.epochEnabled || s.subjectHourEnabled;
    }),
    'uuid',
    'name',
  );

  const subjectValue = subjectsOptions.find((option) => {
    return option.value === values.subject?.uuid;
  });

  useEffect(() => {
    if (!values.elective) {
      setFieldValue(`studentCount`, null, true);
      setFieldTouched(`studentCount`, false);
    }
  }, [values.elective, setFieldTouched, setFieldValue]);

  return (
    <Grid useFormGap>
      <GridRow>
        <GridColumn width={10}>
          <GridRow spacingBottom='s'>
            <Select
              options={subjectsOptions}
              readonly={status === LessonEditStatus.Blocked}
              name='subject'
              isSearchable
              onChange={async (option) => {
                const opt = option as SingleValue<SelectOptionType>;
                await setFieldTouched(`subject`, true);
                if (opt) {
                  const subject = subjectData?.subjects.find((s) => s.uuid === opt.value);
                  await setFieldValue(`subject`, { uuid: subject?.uuid, name: subject?.name }, true);
                } else {
                  await setFieldValue(`subject`, null, true);
                }
              }}
              value={subjectValue}
              label={t('lesson.basics.name')}
              // Ohne onBlur wuerde touched.subject nur beim Auswaehlen gesetzt - die Pflichtfeld-
              // Meldung erschiene dann nie, weil sie genau im leeren Zustand gebraucht wird.
              onBlur={() => setFieldTouched('subject', true)}
              // Pflichtfeld: Meldung am Feld statt als Sammelhinweis in der Fusszeile. Das Schema
              // validiert die verschachtelte subject.uuid, deren Meldung hier mit abgedeckt wird.
              error={
                touched.subject && errors.subject
                  ? typeof errors.subject === 'string'
                    ? errors.subject
                    : t('validation.common.required')
                  : undefined
              }
            />
          </GridRow>
          <GridRow spacingTop='s'>
            <GridColumn width={6}>
              <Input
                value={values.subTitle}
                name='subTitle'
                onChange={handleChange}
                label={t('lesson.basics.subTitle')}
                readonly={status === LessonEditStatus.Blocked}
              />
            </GridColumn>
            <GridColumn width={6}>
              <Input
                value={values.comment}
                name='comment'
                onChange={handleChange}
                label={t('lesson.basics.comment')}
                readonly={status === LessonEditStatus.Blocked}
              />
            </GridColumn>
          </GridRow>
        </GridColumn>

        <GridColumn width={2} className='mt-3'>
          <Checkbox
            name='teachingLoadEnabled'
            checked={values.teachingLoadEnabled ?? false}
            label={t('lesson.basics.isTeachingLoadEnabled.full')}
            onChange={async (event) => {
              await setFieldValue(`teachingLoadEnabled`, event.target.checked, true);
              await setFieldTouched(`teachingLoadEnabled`, true);
            }}
          />
          <Checkbox
            name='timetableEnabled'
            disabled={
              status === LessonEditStatus.PlacedCards ||
              status === LessonEditStatus.Blocked ||
              status === LessonEditStatus.InVersion ||
              !!timetableDisabled
            }
            checked={values.timetableEnabled ?? false}
            label={t('lesson.basics.isTimetableEnabled.full')}
            onChange={async (event) => {
              await setFieldValue(`timetableEnabled`, event.target.checked, true);
              await setFieldTouched(`timetableEnabled`, true);
            }}
          />
          <Checkbox
            name='elective'
            checked={values.elective ?? false}
            label={t('lesson.basics.isElective')}
            onChange={async (event) => {
              await setFieldValue(`elective`, event.target.checked, true);
              await setFieldTouched(`elective`, true);
            }}
          />
          {values.elective && (
            <Input
              label={t('lesson.basics.studentCount')}
              name={'studentCount'}
              value={values.studentCount}
              onChange={handleChange}
            />
          )}
        </GridColumn>
      </GridRow>
    </Grid>
  );
};
